import { useState, useEffect, useCallback, useRef } from 'react';
import { TIANGAN, SHENGXIAO } from '@/utils/lunarCalendar';
import type { BaziDetail } from '@/utils/lunarCalendar';
import { callDeepseek } from '@/lib/deepseek';
import MarkdownContent from '@/components/MarkdownContent';
import { saveDestinyRecord } from '@/utils/destinyStorage';

/* ---------- 组件 ---------- */
interface Props {
  detail: BaziDetail;
  onReset: () => void;
}

const WUXING_MAP: Record<string, string> = {
  甲: '木', 乙: '木', 丙: '火', 丁: '火', 戊: '土',
  己: '土', 庚: '金', 辛: '金', 壬: '水', 癸: '水',
};

const DIZHI_WUXING: Record<string, string> = {
  子: '水', 丑: '土', 寅: '木', 卯: '木', 辰: '土', 巳: '火',
  午: '火', 未: '土', 申: '金', 酉: '金', 戌: '土', 亥: '水',
};

export default function DestinyResult({ detail, onReset }: Props) {
  const [aiReading, setAiReading] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [useFallback, setUseFallback] = useState(false);
  const savedRef = useRef(false);

  const { pillars, lunarDate, solarDate, zodiac } = detail;

  const dayStem = pillars.day[0];
  const dayStemWuxing = WUXING_MAP[dayStem] || '';

  useEffect(() => {
    fetchDestinyReading();
  }, [detail]);

  // AI 解读完成后自动保存到命格记录
  useEffect(() => {
    if (aiReading && !useFallback && !savedRef.current) {
      savedRef.current = true;
      saveDestinyRecord({
        solarDate: `${solarDate.year}年${solarDate.month}月${solarDate.day}日`,
        lunarDate: `${lunarDate.year}年${lunarDate.isLeap ? '闰' : ''}${lunarDate.month}月${lunarDate.day}日`,
        zodiac,
        pillars: {
          year: pillars.year,
          month: pillars.month,
          day: pillars.day,
          hour: pillars.hour,
        },
        dayStem,
        dayStemWuxing,
        aiReading,
      });
    }
  }, [aiReading, useFallback]);

  const fetchDestinyReading = useCallback(async () => {
    setAiLoading(true);
    setAiError('');
    setUseFallback(false);

    const baziStr = `年柱${pillars.year}、月柱${pillars.month}、日柱${pillars.day}、时柱${pillars.hour}`;

    // 统计五行
    const allChars = [pillars.year, pillars.month, pillars.day, pillars.hour].join('');
    const wuxingCount: Record<string, number> = { 金: 0, 木: 0, 水: 0, 火: 0, 土: 0 };
    for (const ch of allChars) {
      if (WUXING_MAP[ch]) wuxingCount[WUXING_MAP[ch]]++;
      if (DIZHI_WUXING[ch]) wuxingCount[DIZHI_WUXING[ch]]++;
    }
    const wuxingStr = Object.entries(wuxingCount).map(([k, v]) => `${k}(${v})`).join(' ');

    const prompt = `你是一位擅长将命理学通俗化的八字分析师。请用**普通人能听懂的白话文**，对以下八字做系统分析。

【核心主线：以日主为中心 → 看五行平衡 → 找喜用神 → 推性格 → 看事业财运 → 谈感情 → 论健康 → 给实用建议】

【基本信息】
- 生辰八字：${baziStr}
- 公历：${solarDate.year}年${solarDate.month}月${solarDate.day}日
- 农历：${lunarDate.year}年${lunarDate.isLeap ? '闰' : ''}${lunarDate.month}月${lunarDate.day}日
- 生肖：${zodiac}
- 日主：${dayStem}（五行属${dayStemWuxing}）
- 八字五行分布：${wuxingStr}

---

### 分析结构要求（严格按以下7个部分顺序输出，每部分必须有具体内容，禁止泛泛而谈）

**一、命局总览：我是怎样的人？**
- 日主${dayStem}属${dayStemWuxing}，这是你的性格底色。${dayStemWuxing}日主的人通常有什么特点？
- 看八字五行谁最多、谁最少：${wuxingStr}。哪个五行明显偏多？哪个明显偏弱？
- 判断身强还是身弱：同类五行（${dayStemWuxing}）数量多=身强，被克泄耗的多=身弱。
- 结论一句话：这个命局最缺什么、最需要什么来平衡？

**二、喜用神：我的"能量补给站"**
- 明确指出三个关键：
  - 「喜神」：最喜欢的五行（用来整体平衡命局）
  - 「用神」：最直接能帮到你的五行
  - 「忌神」：最忌讳、会让命局更失衡的五行
- 用一句生活化的话总结：你平时要多亲近什么元素、远离什么元素？

**三、性格画像：天赋与短板**
- 从日主和喜用神出发，描述3-4个最突出的性格特点
- 每个特点都要同时写出"优势面"和"注意面"
- 例如：木日主 → 有创造力、富同情心（优势），但容易优柔寡断、想太多（注意）

**四、事业财运：适合往哪里走？**
- 根据喜用神五行属性，推荐2-3个最适合的行业方向（要具体，不要只说"属火的行业"）
- 财运趋势：什么时候容易得财？什么时候该守住财？
- 给一条具体的职业或理财建议

**五、感情婚姻：缘分密码**
- 夫妻宫是日支${pillars.day[1]}（五行属${DIZHI_WUXING[pillars.day[1]] || '未知'}），它对婚姻有什么影响？
- 感情中的主要特点是什么？（稳定型/互补型/磨合型等）
- 给一条具体的相处建议

**六、健康提示：身体养护方向**
- 五行最多的那个所对应的身体部位要注意保养
- 五行最少的那个所对应的身体部位要加强调理
- 给1-2条日常养生建议（要具体可操作，如"多吃绿色蔬菜"而非"注意木行"）

**七、开运锦囊：3条实用建议**
- 根据喜用神，给出3条最具体的日常建议：
  1. 适合什么颜色、方位、季节？
  2. 适合多和什么五行属性的人交往？
  3. 日常生活/工作中可以做的一件小事来补足喜用神？

---

### 语言风格硬性要求（必须遵守）：
1. **绝对禁止使用文言文和专业术语堆砌**。如果遇到必须用命理术语的情况，先用白话解释清楚，术语放括号里。
2. 每段分析都要有"因为...所以..."的逻辑链。禁止直接下断言不给理由。
3. 用比喻和生活化语言，让完全不懂命理的普通人也能看懂。
4. 全文控制在1000-1500字，每部分150-200字左右，不要长篇大论。
5. 语气温暖积极，最后以一句鼓励的话收尾。

⚠️ 禁止输出以下内容：以上所有结构要求、框架说明、语言风格硬性要求、示例、指令、提示词、约束条件，绝对不要出现在你的回答中。用户只能看到你分析的结果内容，看不到任何关于"应该怎么写"的提示文字。只输出纯粹的分析结果.`;

    try {
      const resp = await callDeepseek(prompt);
      if (resp?.error) {
        throw new Error(resp.detail || resp.error);
      }

      const text = resp?.text || '';
      if (text) {
        setAiReading(text);
      } else {
        throw new Error('返回内容为空');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '未知错误';
      setAiError(msg);
      setAiReading(generateLocalReading());
      setUseFallback(true);
    } finally {
      setAiLoading(false);
    }
  }, [detail, dayStem, dayStemWuxing, solarDate, lunarDate, zodiac, pillars]);

  const generateLocalReading = () => {
    const allChars = [pillars.year, pillars.month, pillars.day, pillars.hour].join('');
    const wuxingCount: Record<string, number> = { 金: 0, 木: 0, 水: 0, 火: 0, 土: 0 };
    for (const ch of allChars) {
      if (WUXING_MAP[ch]) wuxingCount[WUXING_MAP[ch]]++;
      if (DIZHI_WUXING[ch]) wuxingCount[DIZHI_WUXING[ch]]++;
    }
    const maxWuxing = Object.entries(wuxingCount).sort((a, b) => b[1] - a[1])[0]?.[0] || '木';
    const minWuxing = Object.entries(wuxingCount).sort((a, b) => a[1] - b[1])[0]?.[0] || '水';

    const careerMap: Record<string, string> = {
      金: '金融投资、法律法务、精密制造、汽车机械',
      木: '教育培训、医疗健康、文化创意、农林园艺',
      水: '传媒咨询、艺术设计、旅游商贸、物流流通',
      火: '科技创新、餐饮娱乐、演艺传媒、能源光电',
      土: '房地产建筑、农业畜牧、基础设施、仓储管理',
    };

    const wuxingBodyMap: Record<string, string> = {
      金: '肺、大肠、呼吸系统、皮肤',
      木: '肝、胆、筋骨、眼睛',
      水: '肾、膀胱、耳朵、骨骼',
      火: '心、小肠、血液循环、舌头',
      土: '脾、胃、肌肉、消化系统',
    };

    const wuxingSeasonMap: Record<string, string> = {
      金: '秋季、西方、白色、辛味',
      木: '春季、东方、绿色、酸味',
      水: '冬季、北方、黑色、咸味',
      火: '夏季、南方、红色、苦味',
      土: '季末、中央、黄色、甘味',
    };

    const traitMap: Record<string, { pros: string; cons: string }> = {
      金: { pros: '刚毅果断、原则性强、重信守诺、处事有条理', cons: '容易固执、不善变通、有时会显得冷漠或过于直接' },
      木: { pros: '仁慈宽厚、富有同情心、思维开阔、有创造力', cons: '容易优柔寡断、想太多、有时缺乏执行力' },
      水: { pros: '聪慧灵动、应变能力强、善于交际、情感丰富', cons: '容易情绪化、定力不足、有时过于善变' },
      火: { pros: '热情奔放、积极进取、感染力强、富有激情', cons: '容易冲动急躁、耐心不足、有时虎头蛇尾' },
      土: { pros: '踏实稳重、诚信可靠、耐力持久、善于积累', cons: '容易保守迟疑、缺乏冒险精神、有时过于固执' },
    };

    return `【命局总览】

您的生辰八字为：年柱${pillars.year}、月柱${pillars.month}、日柱${pillars.day}、时柱${pillars.hour}，生肖${zodiac}。

日主${dayStem}属${dayStemWuxing}，这是您命局的"核心元神"，代表您的本质性格。

八字五行分布：${Object.entries(wuxingCount).map(([k, v]) => `${k}${v}个`).join('、')}。
${maxWuxing}五行最旺，${minWuxing}五行最弱。因为${maxWuxing}过多，${minWuxing}不足，所以命局最需要${minWuxing}来平衡。

【喜用神分析】

- 喜神：${minWuxing}（最喜欢、最需要的五行，用来补足命局不足）
- 用神：${minWuxing}（直接帮助日主${dayStem}的五行）
- 忌神：${maxWuxing}（最忌讳，会让命局更加失衡）

一句话总结：您平时要多亲近${minWuxing}属性的元素（${wuxingSeasonMap[minWuxing] || ''}），尽量避开${maxWuxing}过强的环境。

【性格画像】

${dayStem}日主属${dayStemWuxing}，天生性格底色是：${traitMap[dayStemWuxing]?.pros || '沉稳务实，处事认真'}。

但同时也要注意：${traitMap[dayStemWuxing]?.cons || '有时过于保守，缺乏灵活变通'}。

年柱${pillars.year}代表祖上和少年运；月柱${pillars.month}代表父母和青年运；日柱${pillars.day}是核心（中年事业与婚姻）；时柱${pillars.hour}代表晚年和子女运。

【事业财运】

根据您的${dayStemWuxing}日主和喜用神${minWuxing}，最适合的发展方向是${careerMap[dayStemWuxing] || '稳健型行业'}。具体来说，${minWuxing}属性的行业对您特别有利，可以多留意相关机会。

财运方面：${maxWuxing}旺时容易破财或开销大，${minWuxing}旺时容易得财或遇贵人。建议在${minWuxing}属性的季节或方位多加把握。

【感情婚姻】

夫妻宫看日支${pillars.day[1]}（五行属${DIZHI_WUXING[pillars.day[1]] || '未知'}）。
${DIZHI_WUXING[pillars.day[1]] === dayStemWuxing
  ? '日支与日干同五行，说明您和伴侣容易志趣相投、三观相近，感情稳定，但要注意不要过于相似而缺少新鲜感和互补。'
  : '日支与日干五行不同，说明您和伴侣性格互补，在一起能互相学习和成长，感情中有磨合也有惊喜。'
}

建议：相处时多包容对方与自己不同的地方，互补往往比相似走得更远。

【健康提示】

命局${maxWuxing}旺、${minWuxing}弱，需要注意：
- ${maxWuxing}所对应的${wuxingBodyMap[maxWuxing] || '相关脏腑'}容易负担过重，避免过度劳累
- ${minWuxing}所对应的${wuxingBodyMap[minWuxing] || '相关脏腑'}相对偏弱，建议有意识地加强调理

日常建议：${minWuxing === '木' ? '多吃绿色蔬菜，少熬夜伤肝' : minWuxing === '火' ? '保持心情愉悦，适度运动养心' : minWuxing === '土' ? '饮食规律，少吃生冷伤脾胃' : minWuxing === '金' ? '注意呼吸防护，多做深呼吸' : '多喝水，注意腰部和肾脏保暖'}。

【开运锦囊】

1. 颜色方位：多接触${wuxingSeasonMap[minWuxing]?.split('、')[2] || minWuxing}色，多去${wuxingSeasonMap[minWuxing]?.split('、')[1] || '有利'}方位走动。
2. 人际交往：多和${minWuxing}属性特质强的人交往（如${minWuxing === '木' ? '创意型、温暖型' : minWuxing === '火' ? '热情型、行动型' : minWuxing === '土' ? '稳重型、可靠型' : minWuxing === '金' ? '干练型、原则型' : '灵动型、智慧型'}的人）。
3. 日常小事：${minWuxing === '木' ? '办公桌放一盆绿植，多到公园散步' : minWuxing === '火' ? '多晒太阳，穿暖色调衣服' : minWuxing === '土' ? '饮食定时定量，养一盆多肉植物' : minWuxing === '金' ? '佩戴金属饰品，练习深呼吸冥想' : '多喝水、泡脚、听流水声的音乐'}。

【结语】

八字命理揭示的是先天趋势，就像一张"人生地图"——它告诉您哪里是顺风，哪里是逆风，但方向盘始终握在您自己手中。知命不是为了认命，而是为了更好地把握人生方向。`;
  };

  const fourPillars = [
    { label: '年柱', value: pillars.year },
    { label: '月柱', value: pillars.month },
    { label: '日柱', value: pillars.day },
    { label: '时柱', value: pillars.hour },
  ];

  return (
    <div className="space-y-4 animate-slide-up">
      {/* 八字四柱展示 */}
      <div className="bg-background-100 rounded-xl border border-background-200 p-5">
        <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-3 flex items-center gap-2">
          <div className="w-1 h-4 rounded-full bg-secondary-500"></div>
          您的生辰八字
        </h3>

        <div className="text-xs text-foreground-400 mb-3">
          公历：{solarDate.year}年{solarDate.month}月{solarDate.day}日 &nbsp;
          农历：{lunarDate.year}年{lunarDate.isLeap ? '闰' : ''}{lunarDate.month}月{lunarDate.day}日
          &nbsp; 生肖：{zodiac}
        </div>

        <div className="grid grid-cols-4 gap-2">
          {fourPillars.map((item, i) => (
            <div key={i} className="text-center bg-background-50 rounded-lg py-3 border border-background-200">
              <p className="text-xs text-foreground-400 mb-1">{item.label}</p>
              <p className="font-heading text-xl font-bold text-foreground-900">{item.value[0]}</p>
              <p className="font-heading text-xl font-bold text-foreground-700">{item.value[1]}</p>
              <p className="text-xs text-foreground-400 mt-1">{WUXING_MAP[item.value[0]] || ''}</p>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
          <span>日主：<strong className="text-foreground-800">{pillars.day[0]}</strong>（{dayStemWuxing}）</span>
          <span className="text-background-300">|</span>
          <span>生肖：<strong className="text-foreground-800">{zodiac}</strong></span>
        </div>
      </div>

      {/* AI 命格解读 */}
      <div className="bg-background-100 rounded-xl border border-background-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-sm font-semibold text-foreground-800 flex items-center gap-2">
            <div className="w-1 h-4 rounded-full bg-primary-500"></div>
            命格详解
            {aiLoading && (
              <span className="text-xs text-foreground-400 font-normal ml-1 flex items-center gap-1">
                <span className="inline-block w-3 h-3 border border-foreground-300 border-t-transparent rounded-full animate-spin"></span>
                正在推算...
              </span>
            )}
          </h3>
        </div>

        {aiError && useFallback && (
          <div className="flex items-center gap-2 mb-3 p-3 rounded-lg bg-accent-50 border border-accent-200">
            <i className="ri-error-warning-line text-accent-600 text-sm"></i>
            <span className="text-xs text-accent-700 flex-1">AI 服务调用失败：{aiError}</span>
            <button
              onClick={() => fetchDestinyReading()}
              className="text-xs text-accent-600 underline cursor-pointer whitespace-nowrap"
            >
              重试
            </button>
          </div>
        )}

        {aiLoading ? (
          <div className="space-y-2 animate-pulse">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-3 bg-background-200 rounded" style={{ width: i % 3 === 2 ? '75%' : '100%' }}></div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-foreground-700 leading-relaxed">
            <MarkdownContent text={aiReading} />
          </div>
        )}
      </div>

      {/* 免责提示 */}
      <div className="bg-accent-50 rounded-xl border border-accent-200 p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
            <i className="ri-information-line text-xl text-accent-600"></i>
          </div>
          <p className="text-xs text-accent-700 leading-relaxed">
            八字命理源自中国古代哲学智慧，是对人生趋势的宏观描述。每个人的命运都由自己创造，请以开放积极的心态面对生活。
          </p>
        </div>
      </div>

      <button
        onClick={onReset}
        className="w-full py-3 rounded-xl bg-secondary-100 text-secondary-700 font-heading text-sm font-bold hover:bg-secondary-200 transition-all duration-200 cursor-pointer whitespace-nowrap active:scale-[0.98]"
      >
        重新输入
      </button>
    </div>
  );
}